import React, { useCallback, useState } from 'react'
import PropTypes from 'prop-types'
import dayjs from 'dayjs'
import { Checkbox, FormControlLabel, IconButton, makeStyles } from '@material-ui/core'
import SydSelect from '../../commonDesign/SydSelect'
import SydButton from '../../commonDesign/Button'
import Icon from '../../atoms/Icon'
import { ICON_NAMES } from '../../../constants'
import { GROUPING_PRESETS, QUICK_DATE_OPTIONS, CALCULATION_TYPES } from './lib/constants'
import CustomGroupingDialog from './CustomGroupingDialog'
import ColumnBuilderDialog from './ColumnBuilderDialog'

const GLOBAL_DATE_OPTIONS = [
  ...QUICK_DATE_OPTIONS,
  { value: '__custom__', label: 'Custom Range...' }
]

const useStyles = makeStyles(() => ({
  toolbar: {
    display: 'flex',
    alignItems: 'flex-end',
    gap: '12px',
    padding: '8px 12px',
    borderBottom: '1px solid var(--opal-border-default, #e0e0e0)',
    background: 'var(--opal-surface-default, #fff)',
    flexShrink: 0
  },
  field: {
    display: 'flex',
    flexDirection: 'column',
    gap: '4px',
    minWidth: 200,
    flex: 1,
    maxWidth: 360
  },
  fieldNarrow: {
    display: 'flex',
    flexDirection: 'column',
    gap: '4px',
    minWidth: 120,
    maxWidth: 200,
    flex: '0 0 auto'
  },
  label: {
    fontSize: 11,
    fontWeight: 600,
    textTransform: 'uppercase',
    letterSpacing: 0.5,
    color: 'var(--color-neutral-600, #666)'
  },
  fieldRow: {
    display: 'flex',
    alignItems: 'center',
    gap: 4
  },
  selectWrap: {
    flex: 1,
    minWidth: 0
  },
  editBtn: {
    padding: 4,
    flexShrink: 0
  },
  summary: {
    fontSize: 11,
    color: 'var(--color-neutral-500, #888)',
    marginTop: 2,
    whiteSpace: 'nowrap',
    overflow: 'hidden',
    textOverflow: 'ellipsis'
  },
  columnsSummaryBox: {
    display: 'flex',
    alignItems: 'center',
    gap: 4,
    padding: '6px 8px',
    border: 'var(--border-neutral-thin, 1px solid #ddd)',
    borderRadius: 'var(--border-radius-sm, 4px)',
    background: 'var(--theme-color-surface-d0, #fff)',
    fontSize: 13,
    color: 'var(--color-neutral-700, #555)',
    minHeight: 32,
    cursor: 'pointer',
    flex: 1,
    minWidth: 0,
    overflow: 'hidden',
    '&:hover': {
      borderColor: 'var(--color-primary-500, #1976d2)'
    }
  },
  columnsSummaryText: {
    flex: 1,
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap'
  },
  columnsSummaryPlaceholder: {
    color: 'var(--color-neutral-400, #aaa)'
  },
  columnCount: {
    fontSize: 11,
    color: 'var(--color-neutral-500, #888)',
    flexShrink: 0
  },
  dateRangeRow: {
    display: 'flex',
    gap: 6,
    alignItems: 'center'
  },
  dateInput: {
    flex: 1,
    padding: '6px 8px',
    fontSize: 13,
    border: 'var(--border-neutral-thin, 1px solid #ddd)',
    borderRadius: 'var(--border-radius-sm, 4px)',
    background: 'var(--theme-color-surface-d0, #fff)',
    color: 'inherit',
    outline: 'none',
    minWidth: 0,
    '&:focus': {
      outline: '1px solid var(--color-primary-500, #1976d2)'
    }
  },
  backLink: {
    fontSize: 11,
    color: 'var(--color-primary-500, #1976d2)',
    cursor: 'pointer',
    marginTop: 2,
    '&:hover': { textDecoration: 'underline' }
  },
  actions: {
    display: 'flex',
    alignItems: 'center',
    gap: 8,
    paddingBottom: 2
  },
  toggleLabel: {
    '& .MuiFormControlLabel-label': {
      fontSize: 12,
      color: 'var(--color-neutral-600, #666)'
    }
  }
}))

function getColumnSummary (columns) {
  if (!columns || columns.length === 0) return null
  return columns.map(col => {
    const typeDef = CALCULATION_TYPES[col.type]
    const label = typeDef?.label || col.type
    const opts = col.options
    if (opts?.dateMode === 'quickDate' && opts?.quickDate) return `${label} (${opts.quickDate})`
    if (opts?.dateMode === 'dateRange' && opts?.startDate && opts?.endDate) {
      return `${label} (${dayjs(opts.startDate).format('M/D')}-${dayjs(opts.endDate).format('M/D')})`
    }
    return label
  }).join(', ')
}

export default function ExplorerConfigToolbar ({
  config,
  setGroupings,
  setGroupingPreset,
  setColumns,
  setQuickDate,
  setDateRange,
  setFetchAllLevels,
  isFetching,
  onRefresh,
  canRun
}) {
  const classes = useStyles()
  const [groupingDialogOpen, setGroupingDialogOpen] = useState(false)
  const [columnDialogOpen, setColumnDialogOpen] = useState(false)

  const isCustomDateRange = config.dateMode === 'dateRange'

  const handlePresetChange = useCallback((e) => {
    const value = e.target.value
    if (value === 'custom') {
      setGroupingDialogOpen(true)
      return
    }
    setGroupingPreset(value)
  }, [setGroupingPreset])

  const handleGroupingApply = useCallback((groupings) => {
    setGroupings(groupings)
  }, [setGroupings])

  const handleColumnsApply = useCallback((columns) => {
    setColumns(columns)
  }, [setColumns])

  const handleGlobalDateChange = useCallback((e) => {
    const value = e.target.value
    if (value === '__custom__') {
      setDateRange(null, null)
      return
    }
    setQuickDate(value)
  }, [setQuickDate, setDateRange])

  const handleStartDateChange = useCallback((e) => {
    setDateRange(e.target.value || null, config.endDate)
  }, [setDateRange, config.endDate])

  const handleEndDateChange = useCallback((e) => {
    setDateRange(config.startDate, e.target.value || null)
  }, [setDateRange, config.startDate])

  const handleBackToPresets = useCallback(() => {
    setQuickDate('YTD')
  }, [setQuickDate])

  const handleFetchAllChange = useCallback((e) => {
    setFetchAllLevels(e.target.checked)
  }, [setFetchAllLevels])

  const groupingSummary = config.groupings.length > 0
    ? config.groupings.join(' > ')
    : 'None'

  const columnSummary = getColumnSummary(config.columns)

  return (
    <div className={classes.toolbar}>
      <div className={classes.field}>
        <label className={classes.label}>Grouping</label>
        <div className={classes.fieldRow}>
          <div className={classes.selectWrap}>
            <SydSelect
              options={GROUPING_PRESETS}
              value={config.groupingPreset}
              onChange={handlePresetChange}
              size='sm'
              minWidth='160px'
            />
          </div>
          <IconButton
            className={classes.editBtn}
            size='small'
            onClick={() => setGroupingDialogOpen(true)}
            title='Edit custom grouping'
          >
            <Icon name='edit' customSize={16} />
          </IconButton>
        </div>
        <div className={classes.summary}>{groupingSummary}</div>
      </div>
      <div className={classes.field}>
        <label className={classes.label}>Columns</label>
        <div className={classes.fieldRow}>
          <div
            className={classes.columnsSummaryBox}
            onClick={() => setColumnDialogOpen(true)}
          >
            {columnSummary
              ? <span className={classes.columnsSummaryText}>{columnSummary}</span>
              : <span className={`${classes.columnsSummaryText} ${classes.columnsSummaryPlaceholder}`}>No columns selected</span>}
            {config.columns.length > 0 && (
              <span className={classes.columnCount}>{config.columns.length}</span>
            )}
          </div>
          <IconButton
            className={classes.editBtn}
            size='small'
            onClick={() => setColumnDialogOpen(true)}
            title='Edit columns'
          >
            <Icon name='edit' customSize={16} />
          </IconButton>
        </div>
      </div>
      <div className={classes.fieldNarrow}>
        <label className={classes.label}>Date Range</label>
        {isCustomDateRange ? (
          <>
            <div className={classes.dateRangeRow}>
              <input
                type='date'
                className={classes.dateInput}
                value={config.startDate || ''}
                onChange={handleStartDateChange}
              />
              <input
                type='date'
                className={classes.dateInput}
                value={config.endDate || ''}
                onChange={handleEndDateChange}
              />
            </div>
            <span className={classes.backLink} onClick={handleBackToPresets}>Back to presets</span>
          </>
        ) : (
          <SydSelect
            options={GLOBAL_DATE_OPTIONS}
            value={config.quickDate}
            onChange={handleGlobalDateChange}
            size='sm'
            minWidth='120px'
          />
        )}
      </div>
      <div className={classes.actions}>
        <FormControlLabel
          className={classes.toggleLabel}
          control={
            <Checkbox
              checked={config.fetchAllLevels}
              onChange={handleFetchAllChange}
              size='small'
              color='primary'
            />
          }
          label='Fetch all'
        />
        <SydButton
          icon={ICON_NAMES.queryStats}
          variant='primary'
          size='sm'
          onClick={onRefresh}
          processing={isFetching}
          disabled={!canRun}
        >
          Run
        </SydButton>
      </div>
      <CustomGroupingDialog
        open={groupingDialogOpen}
        onClose={() => setGroupingDialogOpen(false)}
        currentGroupings={config.groupings}
        onApply={handleGroupingApply}
      />
      <ColumnBuilderDialog
        open={columnDialogOpen}
        onClose={() => setColumnDialogOpen(false)}
        currentColumns={config.columns}
        onApply={handleColumnsApply}
      />
    </div>
  )
}

ExplorerConfigToolbar.propTypes = {
  config: PropTypes.shape({
    groupings: PropTypes.arrayOf(PropTypes.string),
    groupingPreset: PropTypes.string,
    columns: PropTypes.arrayOf(PropTypes.object),
    dateMode: PropTypes.string,
    quickDate: PropTypes.string,
    startDate: PropTypes.string,
    endDate: PropTypes.string,
    fetchAllLevels: PropTypes.bool
  }).isRequired,
  setGroupings: PropTypes.func.isRequired,
  setGroupingPreset: PropTypes.func.isRequired,
  setColumns: PropTypes.func.isRequired,
  setQuickDate: PropTypes.func.isRequired,
  setDateRange: PropTypes.func.isRequired,
  setFetchAllLevels: PropTypes.func.isRequired,
  isFetching: PropTypes.bool,
  onRefresh: PropTypes.func,
  canRun: PropTypes.bool
}
